import {BuyFeatureButtonsTypeToDisable, BuyFeatureView} from './BuyFeatureView';
import {ModuleIsEnabledFilter, ResourcesPack, LockManager} from '@gongxigames/framework/core';
import {isEnabledBuyFeature} from '@gongxigames/framework/slot/modules/buyFeature/isEnabledBuyFeature';
import {GameOnlyEventMap, slotStore, strictUiEmitter} from '@gongxigames/framework/slotV2/globals';
import {SlotModuleV2} from '@gongxigames/framework/slotV2/baseClasses/slotModuleV2/SlotModuleV2';
import {LAYERS} from '@gongxigames/framework/slot';

const buyFeatureShowPopupButton = 'buyFeatureShowPopupButton';
const buyFeatureConfirmButton = 'buyFeatureConfirmButton';

export class BuyFeatureModule<T extends GameOnlyEventMap> extends SlotModuleV2<T> {
    public static readonly NAME: symbol = Symbol('BuyFeatureModule');
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledBuyFeature;
    public static layer = LAYERS.OVER_KEYPAD;

    public static readonly FEATURE_PRICE_UPDATE: symbol = Symbol('FEATURE_PRICE_UPDATE');

    protected view!: BuyFeatureView;

    private lockManager: LockManager = new LockManager();

    public init(): void {
        super.init();

        this.lockManager.add(buyFeatureShowPopupButton, this.onChangeLockButton.bind(this, 'show'));
        this.lockManager.add(buyFeatureConfirmButton, this.onChangeLockButton.bind(this, 'confirm'));
        this.disable();

        this.setBuyFeatureVisibility();

        // TODO use widgets API in future
        if (slotStore.logic_buyFeatureIndependentBet.value) {
            this.view.setStickyPosition(true);
            strictUiEmitter.emit('Game.command.buyFeature.moveToUi', this.view.buttonContainer);
        }
    }

    protected setBuyFeatureVisibility(): void {
        if (slotStore.logic_shouldHideBuyFeature.value) {
            this.hide();
        } else {
            this.show();
        }
    }

    public getResourcesToLoad(): ResourcesPack {
        return {
            default: 'default_priority_buy_feature'
        };
    }

    private onChangeLockButton(type: BuyFeatureButtonsTypeToDisable, lock = true): void {
        if (lock) {
            this.view.disableButton(type);
        } else {
            this.view.enableButton(type);
        }
    }

    public restore(): void {
        if (!slotStore.logic_nextActionIsSpin.value) {
            if (slotStore.logic_buyFeatureIsProgressStarted.value) {
                this.hide();
            } else if (slotStore.logic_shouldHideBuyFeature.value) {
                this.hide();
            } else {
                this.disable();
            }
        }
    }

    protected onInit(): void {
        this.view = new BuyFeatureView({
            onOpened: this.onBuyFeaturePopupOpen.bind(this),
            onYesClicked: this.onBuyFeaturePopupYes.bind(this),
            onNoClicked: this.onBuyFeaturePopupNo.bind(this),
            onClosed: this.onBuyFeaturePopupClose.bind(this),
        });
        this.container.addChild(this.view.container);

        this.updateButtonsState();
    }

    protected onSetupEvents(): void {
        // Todo this enable part will be in the act and special enable map for UI
        this.emitter.on('Game.command.buyFeature.enable', this.enable.bind(this));
        this.emitter.on('Game.command.buyFeature.disable', this.disable.bind(this));
        this.emitter.on('Game.command.buyFeature.hide', this.hide.bind(this));
        this.emitter.on('Game.command.buyFeature.show', this.show.bind(this));
        this.emitter.on('Game.command.buyFeature.priceUpdate', this.updateButtonsState.bind(this));

        // --- TODO maybe remove later
        this.emitter.on('UI.command.totalBetChanged', this.updateButtonsState.bind(this));
        this.emitter.on('UI.command.balanceUpdated', this.updateButtonsState.bind(this));
        strictUiEmitter.on('General.Promotion.Started', this.hide);
        strictUiEmitter.on('General.Promotion.Ended', this.show);
        // --- TODO maybe remove later
    }

    protected onBuyFeaturePopupOpen(): void {
        strictUiEmitter.emit('Game.command.round.featureStarted', {type: 'buyFeaturePopup'});
        strictUiEmitter.emit('UI.buyFeature.open');
    }

    protected onBuyFeaturePopupYes(): void {
        strictUiEmitter.emit('Game.command.round.featureStarted', {type: 'spin'});
        strictUiEmitter.emit('UI.buyFeature.buy');
    }

    protected onBuyFeaturePopupNo(): void {
        strictUiEmitter.emit('Game.command.round.featureStopped', {type: 'buyFeaturePopup'});
        strictUiEmitter.emit('UI.buyFeature.decline');
    }

    protected onBuyFeaturePopupClose(): void {
        strictUiEmitter.emit('UI.buyFeature.close');
    }

    protected enable(): void {
        this.enableShowPopupButtonByName(BuyFeatureModule.NAME);
    }

    protected disable(): void {
        this.disableShowPopupButtonByName(BuyFeatureModule.NAME);
    }

    protected hide(): void {
        this.view.toggleShowButton(false);
    }

    protected show(): void {
        this.view.toggleShowButton(true);
    }

    protected updateButtonsState(): void {
        if (slotStore.logic_canBuyFeatureSpin.value) {
            this.enableShowPopupButtonByName(BuyFeatureModule.FEATURE_PRICE_UPDATE);

            if (this.lockManager.getLock(buyFeatureConfirmButton)) {
                this.lockManager.unlock(buyFeatureConfirmButton, BuyFeatureModule.FEATURE_PRICE_UPDATE);
            }
        } else {
            this.disableShowPopupButtonByName(BuyFeatureModule.FEATURE_PRICE_UPDATE);
            this.lockManager.lock(buyFeatureConfirmButton, BuyFeatureModule.FEATURE_PRICE_UPDATE);
        }

        this.view?.updatePrice(this.store.currencyFormatter.value!.formatCents(slotStore.logic_buyFeaturePrice.value));
    }

    public enableShowPopupButtonByName(reason: symbol): void {
        if (this.lockManager.getLock(buyFeatureShowPopupButton)) {
            this.lockManager.unlock(buyFeatureShowPopupButton, reason);
        }
    }

    public disableShowPopupButtonByName(reason: symbol): void {
        this.lockManager.lock(buyFeatureShowPopupButton, reason);
    }
}
